import { Controller } from "@hotwired/stimulus";

const VIEWPORT_MARGIN_PX = 8;

/**
 * Chip menus are opened via delegation off `data-results-toolbar-dropdown-toggle`, so menus
 * rendered by nested components don't need this controller's identifier.
 */
export default class extends Controller {
  private documentClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private resizeHandler: (() => void) | null = null;

  connect() {
    this.documentClickHandler = (e: MouseEvent) => {
      if (!this.element.contains(e.target as Node)) this.closeAllDropdowns();
    };
    document.addEventListener("click", this.documentClickHandler);

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") this.closeAllDropdowns();
    };
    document.addEventListener("keydown", this.escapeHandler);

    // A menu left open across a resize would keep a shift measured against the old viewport.
    this.resizeHandler = () => this.openDropdowns().forEach((menu) => this.clampToViewport(menu));
    window.addEventListener("resize", this.resizeHandler);
  }

  disconnect() {
    if (this.documentClickHandler) {
      document.removeEventListener("click", this.documentClickHandler);
      this.documentClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler);
      this.escapeHandler = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  onBarClick(event: Event) {
    const target = event.target as HTMLElement;
    // Leave an open menu alone so the item's own download handler runs before the DOM moves.
    if (target.closest(".ag-grid-component-export-dropdown")) return;

    const toggle = target.closest<HTMLElement>("[data-results-toolbar-dropdown-toggle]");
    const dropdown = toggle ? this.dropdownFor(toggle) : null;
    const wasOpen = dropdown ? !dropdown.hidden : false;

    this.closeAllDropdowns();

    if (toggle && dropdown && !wasOpen) {
      dropdown.hidden = false;
      toggle.setAttribute("aria-pressed", "true");
      this.clampToViewport(dropdown);
    }
  }

  /**
   * Keeps a left-anchored menu on screen. CSS can't: the overhang depends on where the chip
   * landed after the toolbar wrapped.
   */
  private clampToViewport(dropdown: HTMLElement) {
    this.clearClamp(dropdown);

    const limit = document.documentElement.clientWidth - VIEWPORT_MARGIN_PX;
    const available = limit - VIEWPORT_MARGIN_PX;
    if (dropdown.getBoundingClientRect().width > available) {
      // min-width beats max-width in the cascade, so the stylesheet's floor has to go too.
      dropdown.style.minWidth = `${available}px`;
      dropdown.style.maxWidth = `${available}px`;
    }

    const rect = dropdown.getBoundingClientRect();
    let shift = Math.min(0, limit - rect.right);
    // Never trade a right overhang for a left one -- the cap above guarantees both fit.
    shift += Math.max(0, VIEWPORT_MARGIN_PX - (rect.left + shift));

    if (shift !== 0) dropdown.style.transform = `translateX(${Math.round(shift)}px)`;
  }

  private clearClamp(dropdown: HTMLElement) {
    dropdown.style.removeProperty("transform");
    dropdown.style.removeProperty("min-width");
    dropdown.style.removeProperty("max-width");
  }

  private openDropdowns(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>(".ag-grid-component-export-dropdown")
    ).filter((dropdown) => !dropdown.hidden);
  }

  private dropdownFor(toggle: HTMLElement): HTMLElement | null {
    return (
      toggle.parentElement?.querySelector<HTMLElement>(
        ".ag-grid-component-export-dropdown"
      ) ?? null
    );
  }

  private closeAllDropdowns() {
    this.element
      .querySelectorAll<HTMLElement>(".ag-grid-component-export-dropdown")
      .forEach((dropdown) => {
        dropdown.hidden = true;
        this.clearClamp(dropdown);
      });
    this.element
      .querySelectorAll<HTMLElement>("[data-results-toolbar-dropdown-toggle]")
      .forEach((toggle) => toggle.setAttribute("aria-pressed", "false"));
  }
}
